/**
 * Lecteur CII (UN/CEFACT Cross Industry Invoice, D16B) — Factur-X / ZUGFeRD 2 /
 * XRechnung en syntaxe CII.
 *
 * Chemin canonique : rsm:CrossIndustryInvoice ›
 *   rsm:SupplyChainTradeTransaction › ram:ApplicableHeaderTrade{Agreement,
 *   Delivery,Settlement} et ram:IncludedSupplyChainTradeLineItem.
 */
import { emptyDocument, emptyParty, type InvoiceDocument, type InvoiceLine, type Party, type TaxBreakdown, type WarningSeverity } from '../model';
import { detectProfile } from '../profile';
import { child, children, ciiDate, num, path, rootName, text, txt, attr } from './dom';

type Warn = (code: string, message: string, severity?: WarningSeverity) => void;

const NS_CII = 'CrossIndustryInvoice';

export function isCii(doc: Document): boolean {
  return rootName(doc) === NS_CII;
}

function readParty(el: Element | null): Party {
  const p = emptyParty();
  if (!el) return p;
  p.name = txt(el, 'Name');
  p.tradingName = txt(el, 'TradingBusinessName');
  p.electronicAddress = (() => {
    const uri = path(el, 'URIUniversalCommunication', 'URIID');
    const value = text(uri);
    return value ? { scheme: attr(uri, 'schemeID') ?? 'EM', value } : undefined;
  })();
  for (const id of children(el, 'ID')) {
    const value = text(id);
    if (value) p.identifiers.push({ scheme: attr(id, 'schemeID') ?? '', value });
  }
  for (const gid of children(el, 'GlobalID')) {
    const value = text(gid);
    if (value) p.identifiers.push({ scheme: attr(gid, 'schemeID') ?? 'global', value });
  }
  const legal = path(el, 'SpecifiedLegalOrganization', 'ID');
  p.legalRegistrationId = text(legal);
  for (const reg of children(el, 'SpecifiedTaxRegistration')) {
    const id = child(reg, 'ID');
    const value = text(id);
    if (!value) continue;
    const scheme = (attr(id, 'schemeID') ?? '').toUpperCase();
    if (scheme === 'VA') p.vatId = value;
    else if (scheme === 'FC') p.taxId = value;
    else if (!p.taxId) p.taxId = value;
  }
  if (p.legalRegistrationId && /^\d{14}$/.test(p.legalRegistrationId)) p.siret = p.legalRegistrationId;

  const addr = child(el, 'PostalTradeAddress');
  if (addr) {
    p.address = {
      line1: txt(addr, 'LineOne'),
      line2: txt(addr, 'LineTwo'),
      line3: txt(addr, 'LineThree'),
      postcode: txt(addr, 'PostcodeCode'),
      city: txt(addr, 'CityName'),
      country: txt(addr, 'CountryID'),
    };
  }
  const contact = child(el, 'DefinedTradeContact');
  if (contact) {
    p.contact = {
      name: txt(contact, 'PersonName'),
      phone: txt(contact, 'TelephoneUniversalCommunication', 'CompleteNumber'),
      email: txt(contact, 'EmailURIUniversalCommunication', 'URIID'),
    };
  }
  return p;
}

function readLine(el: Element): InvoiceLine {
  const line: InvoiceLine = {};
  line.id = txt(el, 'AssociatedDocumentLineDocument', 'LineID');
  line.note = txt(el, 'AssociatedDocumentLineDocument', 'IncludedNote', 'Content');
  const product = child(el, 'SpecifiedTradeProduct');
  if (product) {
    line.name = txt(product, 'Name');
    line.description = txt(product, 'Description');
    line.sellerItemId = txt(product, 'SellerAssignedID');
  }
  const delivery = child(el, 'SpecifiedLineTradeDelivery');
  if (delivery) {
    const qty = child(delivery, 'BilledQuantity');
    line.quantity = num(qty);
    line.unitCode = attr(qty, 'unitCode');
  }
  const agreement = child(el, 'SpecifiedLineTradeAgreement');
  if (agreement) {
    const net = path(agreement, 'NetPriceProductTradePrice');
    line.unitPrice = num(child(net, 'ChargeAmount'));
    line.priceBaseQuantity = num(child(net, 'BasisQuantity'));
    const gross = path(agreement, 'GrossPriceProductTradePrice');
    if (gross) {
      line.grossPrice = num(child(gross, 'ChargeAmount'));
      line.itemDiscount = num(path(gross, 'AppliedTradeAllowanceCharge', 'ActualAmount'));
    }
  }
  const settlement = child(el, 'SpecifiedLineTradeSettlement');
  if (settlement) {
    line.lineNetAmount = num(path(settlement, 'SpecifiedTradeSettlementLineMonetarySummation', 'LineTotalAmount'));
    const tax = child(settlement, 'ApplicableTradeTax');
    line.vatCategory = txt(tax, 'CategoryCode');
    line.vatRate = num(child(tax, 'RateApplicablePercent'));
  }
  return line;
}

function pickTaxTotal(settlement: Element | null, currency: string | undefined): { main?: Element; other?: Element } {
  const candidates = children(settlement, 'SpecifiedTradeSettlementHeaderMonetarySummation')
    .flatMap((s) => children(s, 'TaxTotalAmount'));
  if (candidates.length === 0) return {};
  const main = candidates.find((c) => attr(c, 'currencyID') === currency) ?? candidates[0];
  const other = candidates.find((c) => c !== main) ?? undefined;
  return { main, other };
}

export function parseCii(doc: Document, warn: Warn): InvoiceDocument {
  const docout = emptyDocument('CII');
  docout.syntaxVersion = 'CII D16B';
  const root = doc.documentElement;
  const transaction = child(root, 'SupplyChainTradeTransaction');
  const exchanged = child(root, 'ExchangedDocument');

  // --- Contexte : BT-23 / BT-24 ---
  const context = child(root, 'ExchangedDocumentContext');
  const bt24 = txt(context, 'GuidelineSpecifiedDocumentContextParameter', 'ID');
  docout.profile = detectProfile(bt24);
  docout.businessProcess = txt(context, 'BusinessProcessSpecifiedDocumentContextParameter', 'ID');
  if (bt24 == null) warn('PROFILE_UNKNOWN', 'BT-24 absent du XML CII');
  else if (docout.profile.profile === 'UNKNOWN') warn('PROFILE_UNKNOWN', `BT-24 non reconnu : ${bt24}`);
  if (docout.profile.family === 'zugferd1') warn('LEGACY_ZUGFERD1', 'Document ZUGFeRD 1.0');

  // --- En-tête ---
  docout.number = txt(exchanged, 'ID');
  const typeCode = txt(exchanged, 'TypeCode');
  docout.documentTypeCode = typeCode;
  docout.documentType = typeCode === '381' ? 'credit-note' : typeCode ? 'invoice' : 'unknown';
  docout.issueDate = ciiDate(child(exchanged, 'IssueDateTime'));
  for (const note of children(exchanged, 'IncludedNote')) {
    const content = txt(note, 'Content');
    if (content) docout.notes.push(content);
  }

  const agreement = child(transaction, 'ApplicableHeaderTradeAgreement');
  const delivery = child(transaction, 'ApplicableHeaderTradeDelivery');
  const settlement = child(transaction, 'ApplicableHeaderTradeSettlement');

  docout.seller = readParty(child(agreement, 'SellerTradeParty'));
  docout.buyer = readParty(child(agreement, 'BuyerTradeParty'));
  const payee = child(settlement, 'PayeeTradeParty');
  if (payee) docout.payee = readParty(payee);

  docout.buyerReference = txt(agreement, 'BuyerReference') ?? docout.buyerReference;
  docout.orderReference = txt(agreement, 'BuyerOrderReferencedDocument', 'IssuerAssignedID');
  docout.salesOrderReference = txt(agreement, 'SellerOrderReferencedDocument', 'IssuerAssignedID');
  docout.contractReference = txt(agreement, 'ContractReferencedDocument', 'IssuerAssignedID');

  docout.deliveryDate =
    ciiDate(path(delivery, 'ActualDeliverySupplyChainEvent', 'OccurrenceDateTime')) ??
    ciiDate(path(delivery, 'ActualDeliveryEvent', 'OccurrenceDateTime'));

  // --- Devises / paiement ---
  docout.currency = txt(settlement, 'InvoiceCurrencyCode');
  docout.taxCurrency = txt(settlement, 'TaxCurrencyCode');

  const means = children(settlement, 'SpecifiedTradeSettlementPaymentMeans');
  const firstMean = means[0] ?? null;
  if (firstMean) {
    docout.payment.meansCode = txt(firstMean, 'TypeCode');
    docout.payment.meansText = txt(firstMean, 'Information');
    docout.payment.iban = txt(firstMean, 'PayeePartyCreditorFinancialAccount', 'IBANID');
    docout.payment.bic = txt(firstMean, 'PayeeSpecifiedCreditorFinancialInstitution', 'BICID');
    docout.payment.remittanceInformation = txt(firstMean, 'Information');
  }
  const terms = child(settlement, 'SpecifiedTradePaymentTerms');
  if (terms) {
    docout.payment.terms = txt(terms, 'Description');
    docout.payment.dueDate = ciiDate(child(terms, 'DueDateDateTime'));
    docout.payment.mandateReference = txt(terms, 'DirectDebitMandateID');
  }
  docout.dueDate = docout.payment.dueDate;

  // --- TVA (BG-23) ---
  const taxes = children(settlement, 'ApplicableTradeTax');
  docout.taxes = taxes
    .map<TaxBreakdown>((t) => ({
      category: txt(t, 'CategoryCode'),
      rate: num(child(t, 'RateApplicablePercent')),
      taxableAmount: num(child(t, 'BasisAmount')),
      taxAmount: num(child(t, 'CalculatedAmount')),
      exemptionReason: txt(t, 'ExemptionReason'),
      exemptionCode: txt(t, 'ExemptionReasonCode'),
    }))
    // Les lignes de TVA sans montant (ex. mention d'exonération au niveau en-tête)
    // restent utiles : on ne filtre que les entrées totalement vides.
    .filter((t) => t.category || t.taxableAmount || t.taxAmount || t.exemptionReason);

  // --- Totaux ---
  const sum = child(settlement, 'SpecifiedTradeSettlementHeaderMonetarySummation');
  if (sum) {
    docout.totals.lineNet = num(child(sum, 'LineTotalAmount'));
    docout.totals.allowanceTotal = num(child(sum, 'AllowanceTotalAmount'));
    docout.totals.chargeTotal = num(child(sum, 'ChargeTotalAmount'));
    docout.totals.taxBasis = num(child(sum, 'TaxBasisTotalAmount'));
    const totals = pickTaxTotal(settlement, docout.currency);
    docout.totals.taxTotal = num(totals.main) ?? undefined;
    docout.totals.taxTotalAccounting = num(totals.other) ?? undefined;
    docout.totals.grandTotal = num(child(sum, 'GrandTotalAmount'));
    docout.totals.prepaid = num(child(sum, 'TotalPrepaidAmount'));
    docout.totals.rounding = num(child(sum, 'RoundingAmount'));
    docout.totals.duePayable = num(child(sum, 'DuePayableAmount'));
  }

  // --- Lignes (BG-25) ---
  docout.lines = children(transaction, 'IncludedSupplyChainTradeLineItem').map(readLine);

  if (docout.lines.length === 0 && docout.profile.profile !== 'MINIMUM' && docout.profile.profile !== 'BASIC_WL') {
    warn('EMPTY_LINES', 'Aucune ligne de facture dans le XML CII');
  }
  return docout;
}
